import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft, Shield, Clock, CheckCircle, XCircle, 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap,
  Award, FileText, ExternalLink, Loader2, AlertTriangle,
  Linkedin, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCoachApplicationsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();
  
  const [applications, setApplications] = useState([]);
  const [allStats, setAllStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coach-applications`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAllStats({
          pending: data.filter(a => a.status === 'pending').length,
          approved: data.filter(a => a.status === 'approved').length,
          rejected: data.filter(a => a.status === 'rejected').length,
          total: data.length
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchApplications();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
    }
  }, [filter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/admin/coach-applications`
        : `${API_URL}/api/admin/coach-applications?status=${filter}`;
      
      const response = await fetch(url, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      } else if (response.status === 401) {
        toast.error(t('admin.toastUnauthorized'));
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error(t('admin.toastLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/coach-applications/${applicationId}/approve`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        toast.success(t('admin.toastApproved'));
        setSelectedApp(null);
        fetchApplications();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin.toastApproveError'));
      }
    } catch (error) {
      toast.error(t('admin.toastApproveError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (applicationId) => {
    if (!rejectionReason.trim()) {
      toast.warning(t('admin.toastRejectReasonRequired'));
      return;
    }
    
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/coach-applications/${applicationId}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ reason: rejectionReason }),
        }
      );
      
      if (response.ok) {
        toast.success(t('admin.toastRejected'));
        setSelectedApp(null);
        setRejectionReason('');
        fetchApplications();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin.toastRejectError'));
      }
    } catch (error) {
      toast.error(t('admin.toastRejectError'));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            <Clock className="w-3 h-3" />
            {t('admin.pending')}
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            <CheckCircle className="w-3 h-3" />
            {t('admin.approved')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            <XCircle className="w-3 h-3" />
            {t('admin.rejected')}
          </span>
        );
      default:
        return null;
    }
  };

  const stats = allStats;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2" data-testid="admin-restricted-title">{t('admin.restrictedAccess')}</h2>
          <p className="text-muted-foreground mb-4">{t('admin.loginToAccess')}</p>
          <Link to="/login">
            <Button className="btn-primary">{t('admin.login')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)} 
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-button"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <span className="font-bold uppercase">{t('admin.administration')}</span>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('admin.loggedIn')} {user?.name || user?.email}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2" data-testid="admin-page-title">{t('admin.coachApplications')}</h1>
          <p className="text-muted-foreground">{t('admin.manageApplications')}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div 
            className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${filter === 'pending' ? 'border-yellow-500 bg-yellow-500/10' : 'border-border hover:border-muted-foreground'}`}
            onClick={() => setFilter('pending')}
            data-testid="filter-pending"
          >
            <div className="flex items-center gap-2 text-yellow-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{t('admin.pending')}</span>
            </div>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </div>
          
          <div 
            className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${filter === 'approved' ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-muted-foreground'}`}
            onClick={() => setFilter('approved')}
            data-testid="filter-approved"
          >
            <div className="flex items-center gap-2 text-green-400 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{t('admin.approved')}</span>
            </div>
            <div className="text-2xl font-bold">{stats.approved}</div>
          </div>
          
          <div 
            className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${filter === 'rejected' ? 'border-red-500 bg-red-500/10' : 'border-border hover:border-muted-foreground'}`}
            onClick={() => setFilter('rejected')}
            data-testid="filter-rejected"
          >
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <XCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{t('admin.rejected')}</span>
            </div>
            <div className="text-2xl font-bold">{stats.rejected}</div>
          </div>
          
          <div 
            className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${filter === 'all' ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            <div className="flex items-center gap-2 text-primary mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">{t('admin.all')}</span>
            </div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : applications.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">{t('admin.noApplications')}</h3>
            <p className="text-muted-foreground">
              {filter === 'pending' 
                ? t('admin.noApplicationsPending')
                : filter === 'approved' ? t('admin.noApplicationsApproved')
                : filter === 'rejected' ? t('admin.noApplicationsRejected')
                : ''
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <div 
                key={app.application_id}
                className="bg-card border border-border rounded-xl overflow-hidden hover:border-muted-foreground transition-all"
                data-testid={`application-card-${app.application_id}`}
              >
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {getStatusBadge(app.status)}
                        <span className="text-xs text-muted-foreground">
                          {t('admin.submittedOn')} {formatDate(app.submitted_at)}
                        </span>
                      </div>
                      
                      <h3 className="font-bold text-lg mb-2">{app.name}</h3>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {app.email}
                        </span>
                        {app.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {app.phone}
                          </span>
                        )}
                        {app.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {app.location}
                          </span>
                        )}
                        {app.experience_years > 0 && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {app.experience_years} {t('admin.yearsExp')}
                          </span>
                        )}
                      </div>

                      {app.specialties && app.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {app.specialties.map((spec, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-1 rounded text-xs font-medium bg-primary/20 text-primary"
                            >
                              {spec}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-3 text-sm">
                        {app.linkedin_url && (
                          <a 
                            href={app.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-400 hover:underline"
                          >
                            <Linkedin className="w-4 h-4" />
                            LinkedIn
                          </a>
                        )}
                        {app.website_url && (
                          <a 
                            href={app.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <Globe className="w-4 h-4" />
                            {t('admin.website')}
                          </a>
                        )}
                        {app.professional_email && app.professional_email !== app.email && (
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            Pro: {app.professional_email}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex md:flex-col items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedApp(app)}
                        data-testid={`view-details-${app.application_id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {t('admin.details')}
                      </Button>
                      
                      {app.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(app.application_id)}
                            disabled={processing}
                            data-testid={`approve-${app.application_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t('admin.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setSelectedApp({ ...app, showRejectForm: true })}
                            disabled={processing}
                            data-testid={`reject-btn-${app.application_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('admin.reject')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {app.status === 'rejected' && app.rejection_reason && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-1">
                        <AlertTriangle className="w-4 h-4" />
                        {t('admin.rejectionReason')}
                      </div>
                      <p className="text-sm text-muted-foreground">{app.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div 
            className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="application-detail-modal"
          >
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-primary" />
                <h2 className="font-bold text-lg">{t('admin.applicationDetails')}</h2>
              </div>
              <button 
                onClick={() => { setSelectedApp(null); setRejectionReason(''); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="close-modal"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedApp.status)}
                <span className="text-sm text-muted-foreground">
                  ID: {selectedApp.application_id}
                </span>
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  {t('admin.personalInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('admin.fullName')}</span>
                    <p className="font-medium">{selectedApp.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('admin.email')}</span>
                    <p className="font-medium">{selectedApp.email}</p>
                  </div>
                  {selectedApp.professional_email && (
                    <div>
                      <span className="text-muted-foreground">{t('admin.professionalEmail')}</span>
                      <p className="font-medium">{selectedApp.professional_email}</p>
                    </div>
                  )}
                  {selectedApp.phone && (
                    <div>
                      <span className="text-muted-foreground">{t('admin.phone')}</span>
                      <p className="font-medium">{selectedApp.phone}</p>
                    </div>
                  )}
                  {selectedApp.location && (
                    <div>
                      <span className="text-muted-foreground">{t('admin.locationLabel')}</span>
                      <p className="font-medium">{selectedApp.location}</p>
                    </div>
                  )}
                </div>
              </div>

              {(selectedApp.linkedin_url || selectedApp.website_url) && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-primary" />
                    {t('admin.externalLinks')}
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    {selectedApp.linkedin_url && (
                      <a 
                        href={selectedApp.linkedin_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                      >
                        <Linkedin className="w-4 h-4" />
                        {t('admin.linkedinProfile')}
                      </a>
                    )}
                    {selectedApp.website_url && (
                      <a 
                        href={selectedApp.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                        {t('admin.website')}
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-primary" />
                  {t('admin.professionalProfile')}
                </h3>
                <div className="space-y-4">
                  {selectedApp.experience_years > 0 && (
                    <div>
                      <span className="text-muted-foreground text-sm">{t('admin.yearsOfExperience')}</span>
                      <p className="font-medium">{selectedApp.experience_years} {t('admin.years')}</p>
                    </div>
                  )}
                  {selectedApp.bio && (
                    <div>
                      <span className="text-muted-foreground text-sm">{t('admin.bio')}</span>
                      <p className="mt-1 text-sm bg-muted/30 p-3 rounded-lg">{selectedApp.bio}</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedApp.specialties && selectedApp.specialties.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" />
                    {t('admin.specialties')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedApp.specialties.map((spec, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedApp.certifications && selectedApp.certifications.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" />
                    {t('admin.certifications')}
                  </h3>
                  <ul className="space-y-2">
                    {selectedApp.certifications.map((cert, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedApp.motivation && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    {t('admin.motivation')}
                  </h3>
                  <p className="text-sm bg-muted/30 p-3 rounded-lg">{selectedApp.motivation}</p>
                </div>
              )}

              <div className="text-xs text-muted-foreground border-t border-border pt-4">
                <p>{t('admin.submittedAt')} {formatDate(selectedApp.submitted_at)}</p>
                {selectedApp.reviewed_at && (
                  <p>{t('admin.reviewedAt')} {formatDate(selectedApp.reviewed_at)}</p>
                )}
              </div>

              {selectedApp.status === 'rejected' && selectedApp.rejection_reason && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 font-medium mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    {t('admin.rejectionReason')}
                  </div>
                  <p className="text-sm">{selectedApp.rejection_reason}</p>
                </div>
              )}

              {selectedApp.showRejectForm && selectedApp.status === 'pending' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 font-medium mb-3">
                    <AlertTriangle className="w-4 h-4" />
                    {t('admin.rejectApplication')}
                  </div>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder={t('admin.rejectionPlaceholder')}
                    className="w-full p-3 rounded-lg bg-background border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={3}
                    data-testid="rejection-reason-input"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedApp({ ...selectedApp, showRejectForm: false }); setRejectionReason(''); }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleReject(selectedApp.application_id)}
                      disabled={processing || !rejectionReason.trim()}
                      data-testid="confirm-reject"
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                      {t('admin.confirmReject')}
                    </Button>
                  </div>
                </div>
              )}

              {selectedApp.status === 'pending' && !selectedApp.showRejectForm && (
                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedApp(null)}
                  >
                    {t('admin.close')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setSelectedApp({ ...selectedApp, showRejectForm: true })}
                    disabled={processing}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    {t('admin.reject')}
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleApprove(selectedApp.application_id)}
                    disabled={processing}
                    data-testid="modal-approve"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                    {t('admin.approve')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
